import type { CalloutTone, Locale } from "../../schema/core.ts";

/** Visitor-facing copy of the learner site and the shared primitives. */
export interface SiteCopy {
  skipToContent: string;
  header: {
    mainSite: string;
    switchLanguage: string;
    navigation: string;
  };
  account: {
    signIn: string;
    /** Accessible name of the account button, with the signed-in user's name. */
    menu: (name: string) => string;
    signedInAs: string;
    builder: string;
    /** The role page, owners only. */
    users: string;
    signOut: string;
    signedOut: string;
  };
  footer: {
    backTo: (host: string) => string;
  };
  common: {
    loading: string;
    retry: string;
    errorTitle: string;
    errorBody: string;
    minutes: (minutes: number) => string;
    close: string;
    dismiss: string;
    notifications: string;
  };
  home: {
    title: string;
    intro: string;
    emptyTitle: string;
    emptyBody: string;
    viewCourse: string;
    lessonCount: (count: number) => string;
  };
  course: {
    lessonsHeading: string;
    progressHeading: string;
    progress: (done: number, total: number) => string;
    start: string;
    continue: string;
    restart: string;
    completedTitle: string;
    completedBody: string;
    saveProgressTitle: string;
    saveProgressBody: string;
    saveProgressCta: string;
    lessonDone: string;
    lessonNumber: (position: number) => string;
    noLessons: string;
  };
  lesson: {
    breadcrumb: string;
    lessonOf: (position: number, total: number) => string;
    finish: string;
    finished: string;
    courseFinished: string;
    finishError: string;
    alreadyDone: string;
    previous: string;
    next: string;
    backToCourse: string;
    signedOutNote: string;
    signedOutCta: string;
    pagination: string;
    lessonsInCourse: string;
  };
  blocks: {
    callout: Record<CalloutTone, string>;
    video: string;
    watchVideo: string;
    opensInNewTab: string;
    quiz: {
      label: string;
      check: string;
      correct: string;
      incorrect: string;
      tryAgain: string;
    };
  };
  login: {
    title: string;
    tabsLabel: string;
    signInTab: string;
    registerTab: string;
    signInIntro: string;
    registerIntro: string;
    name: string;
    email: string;
    password: string;
    passwordHint: string;
    submitSignIn: string;
    submitRegister: string;
    welcome: (name: string) => string;
    errors: {
      invalidCredentials: string;
      userExists: string;
      passwordTooShort: string;
      passwordTooLong: string;
      invalidEmail: string;
      tooManyAttempts: string;
      generic: string;
    };
  };
  /** Signed in, but the role is too low; the body names what the page is for. */
  noAccess: {
    title: string;
    builder: string;
    roles: string;
    back: string;
  };
  notFound: {
    title: string;
    body: string;
    back: string;
  };
  /** A page that failed to load or render (e.g. its code changed with a new release). */
  routeError: {
    title: string;
    body: string;
    reload: string;
  };
}

export const siteCopy: Record<Locale, SiteCopy> = {
  nl: {
    skipToContent: "Naar de inhoud",
    header: {
      mainSite: "Naar de hoofdsite",
      switchLanguage: "Switch to English",
      navigation: "Hoofdmenu",
    },
    account: {
      signIn: "Inloggen",
      menu: (name) => `Accountmenu van ${name}`,
      signedInAs: "Ingelogd als",
      builder: "Cursusbouwer",
      users: "Gebruikers en rollen",
      signOut: "Uitloggen",
      signedOut: "Je bent uitgelogd.",
    },
    footer: {
      backTo: (host) => `Terug naar ${host}`,
    },
    common: {
      loading: "Bezig met laden…",
      retry: "Opnieuw proberen",
      errorTitle: "Er ging iets mis",
      errorBody:
        "We konden dit niet laden. Controleer je internetverbinding en probeer het opnieuw.",
      minutes: (minutes) => `${minutes} min`,
      close: "Sluiten",
      dismiss: "Melding sluiten",
      notifications: "Meldingen",
    },
    home: {
      title: "Cursussen",
      intro: "Leer in je eigen tempo hoe je het bodemleven op je land kunt lezen en herstellen.",
      emptyTitle: "Nog geen cursussen",
      emptyBody: "Er staan nog geen cursussen online. Kom binnenkort terug.",
      viewCourse: "Bekijk de cursus",
      lessonCount: (count) => (count === 1 ? "1 les" : `${count} lessen`),
    },
    course: {
      lessonsHeading: "Lessen",
      progressHeading: "Je voortgang",
      progress: (done, total) => `${done} van ${total} lessen afgerond`,
      start: "Start de cursus",
      continue: "Ga verder",
      restart: "Bekijk de eerste les opnieuw",
      completedTitle: "Cursus afgerond",
      completedBody:
        "Je hebt alle lessen afgerond. Goed gedaan! Je kunt de lessen altijd opnieuw lezen.",
      saveProgressTitle: "Bewaar je voortgang",
      saveProgressBody:
        "Alle lessen zijn vrij te lezen. " +
        "Met een gratis account houden we bij welke lessen je al hebt afgerond.",
      saveProgressCta: "Inloggen of account maken",
      lessonDone: "Afgerond",
      lessonNumber: (position) => `Les ${position}`,
      noLessons: "Deze cursus heeft nog geen lessen.",
    },
    lesson: {
      breadcrumb: "Kruimelpad",
      lessonOf: (position, total) => `Les ${position} van ${total}`,
      finish: "Les afronden",
      finished: "Les afgerond.",
      courseFinished: "Je hebt de laatste les afgerond. Goed gedaan!",
      finishError: "Opslaan is niet gelukt. Probeer het opnieuw.",
      alreadyDone: "Je hebt deze les afgerond.",
      previous: "Vorige les",
      next: "Volgende les",
      backToCourse: "Terug naar het overzicht",
      signedOutNote: "Je kunt alles vrij lezen. Wil je bijhouden welke lessen je hebt afgerond?",
      signedOutCta: "Log in of maak een account",
      pagination: "Vorige en volgende les",
      lessonsInCourse: "Lessen in deze cursus",
    },
    blocks: {
      callout: { tip: "Tip", note: "Goed om te weten", warning: "Let op" },
      video: "Video",
      watchVideo: "Bekijk de video",
      opensInNewTab: "(opent in een nieuw tabblad)",
      quiz: {
        label: "Vraag",
        check: "Controleer",
        correct: "Goed zo!",
        incorrect: "Niet helemaal.",
        tryAgain: "Probeer opnieuw",
      },
    },
    login: {
      title: "Inloggen",
      tabsLabel: "Inloggen of account aanmaken",
      signInTab: "Inloggen",
      registerTab: "Account aanmaken",
      signInIntro: "Log in om verder te gaan waar je gebleven was.",
      registerIntro: "Met een gratis account houden we bij welke lessen je hebt afgerond.",
      name: "Naam",
      email: "E-mailadres",
      password: "Wachtwoord",
      passwordHint: "Minstens 8 tekens.",
      submitSignIn: "Inloggen",
      submitRegister: "Account aanmaken",
      welcome: (name) => `Welkom, ${name}!`,
      errors: {
        invalidCredentials:
          "Dit e-mailadres en wachtwoord horen niet bij elkaar. Controleer ze en probeer het opnieuw.",
        userExists: "Er is al een account met dit e-mailadres. Log in via het tabblad Inloggen.",
        passwordTooShort: "Kies een wachtwoord van minstens 8 tekens.",
        passwordTooLong: "Dit wachtwoord is te lang. Kies een korter wachtwoord.",
        invalidEmail: "Vul een geldig e-mailadres in.",
        tooManyAttempts: "Te veel pogingen. Wacht even en probeer het dan opnieuw.",
        generic: "Er ging iets mis. Probeer het opnieuw.",
      },
    },
    noAccess: {
      title: "Geen toegang",
      builder:
        "De cursusbouwer is alleen voor beheerders. Denk je dat je toegang hoort te hebben? " +
        "Neem dan contact op met de beheerder.",
      roles: "Alleen de site-eigenaar wijst beheerders aan.",
      back: "Naar de cursussen",
    },
    notFound: {
      title: "Pagina niet gevonden",
      body: "Deze pagina bestaat niet (meer). Misschien is de link verouderd.",
      back: "Naar de cursussen",
    },
    routeError: {
      title: "Deze pagina kon niet laden",
      body:
        "Er ging iets mis bij het openen van deze pagina. Misschien is de site net bijgewerkt. " +
        "Laad de pagina opnieuw om het nog eens te proberen.",
      reload: "Pagina opnieuw laden",
    },
  },
  en: {
    skipToContent: "Skip to content",
    header: {
      mainSite: "Go to the main site",
      switchLanguage: "Schakel naar Nederlands",
      navigation: "Main navigation",
    },
    account: {
      signIn: "Sign in",
      menu: (name) => `Account menu for ${name}`,
      signedInAs: "Signed in as",
      builder: "Course builder",
      users: "Users and roles",
      signOut: "Sign out",
      signedOut: "You are signed out.",
    },
    footer: {
      backTo: (host) => `Back to ${host}`,
    },
    common: {
      loading: "Loading…",
      retry: "Try again",
      errorTitle: "Something went wrong",
      errorBody: "We could not load this. Check your internet connection and try again.",
      minutes: (minutes) => `${minutes} min`,
      close: "Close",
      dismiss: "Dismiss notification",
      notifications: "Notifications",
    },
    home: {
      title: "Courses",
      intro: "Learn at your own pace how to read and restore the soil life on your land.",
      emptyTitle: "No courses yet",
      emptyBody: "There are no courses online yet. Please check back soon.",
      viewCourse: "View course",
      lessonCount: (count) => (count === 1 ? "1 lesson" : `${count} lessons`),
    },
    course: {
      lessonsHeading: "Lessons",
      progressHeading: "Your progress",
      progress: (done, total) => `${done} of ${total} lessons completed`,
      start: "Start the course",
      continue: "Continue",
      restart: "Revisit the first lesson",
      completedTitle: "Course completed",
      completedBody:
        "You have completed every lesson. Well done! You can always read the lessons again.",
      saveProgressTitle: "Keep your progress",
      saveProgressBody:
        "All lessons are free to read. " +
        "With a free account we remember which lessons you have completed.",
      saveProgressCta: "Sign in or create an account",
      lessonDone: "Completed",
      lessonNumber: (position) => `Lesson ${position}`,
      noLessons: "This course has no lessons yet.",
    },
    lesson: {
      breadcrumb: "Breadcrumb",
      lessonOf: (position, total) => `Lesson ${position} of ${total}`,
      finish: "Complete lesson",
      finished: "Lesson completed.",
      courseFinished: "You completed the last lesson. Well done!",
      finishError: "Saving did not work. Please try again.",
      alreadyDone: "You have completed this lesson.",
      previous: "Previous lesson",
      next: "Next lesson",
      backToCourse: "Back to the overview",
      signedOutNote:
        "You can read everything for free. Want to keep track of the lessons you have completed?",
      signedOutCta: "Sign in or create an account",
      pagination: "Previous and next lesson",
      lessonsInCourse: "Lessons in this course",
    },
    blocks: {
      callout: { tip: "Tip", note: "Good to know", warning: "Watch out" },
      video: "Video",
      watchVideo: "Watch the video",
      opensInNewTab: "(opens in a new tab)",
      quiz: {
        label: "Question",
        check: "Check",
        correct: "Well done!",
        incorrect: "Not quite.",
        tryAgain: "Try again",
      },
    },
    login: {
      title: "Sign in",
      tabsLabel: "Sign in or create an account",
      signInTab: "Sign in",
      registerTab: "Create account",
      signInIntro: "Sign in to continue where you left off.",
      registerIntro: "With a free account we remember which lessons you have completed.",
      name: "Name",
      email: "Email address",
      password: "Password",
      passwordHint: "At least 8 characters.",
      submitSignIn: "Sign in",
      submitRegister: "Create account",
      welcome: (name) => `Welcome, ${name}!`,
      errors: {
        invalidCredentials:
          "This email address and password do not match. Please check them and try again.",
        userExists: "An account with this email address already exists. Use the Sign in tab.",
        passwordTooShort: "Choose a password of at least 8 characters.",
        passwordTooLong: "This password is too long. Choose a shorter one.",
        invalidEmail: "Enter a valid email address.",
        tooManyAttempts: "Too many attempts. Wait a moment and try again.",
        generic: "Something went wrong. Please try again.",
      },
    },
    noAccess: {
      title: "No access",
      builder:
        "The course builder is for administrators only. Think you should have access? " +
        "Please contact the administrator.",
      roles: "Only the site owner appoints admins.",
      back: "Go to the courses",
    },
    notFound: {
      title: "Page not found",
      body: "This page does not exist (anymore). The link may be out of date.",
      back: "Go to the courses",
    },
    routeError: {
      title: "This page could not load",
      body:
        "Something went wrong while opening this page. The site may just have been updated. " +
        "Reload the page to try again.",
      reload: "Reload page",
    },
  },
};
